import { Injectable, inject } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { NachaRow, NachaMatch, NachaResult } from '../models/nacha.model';
import { CustomerModel, AchMethodModel } from '../../customer/customer.model';
import { CustomersService } from '../../customer/services/customers.service';
import { TransactionsService } from '../../transactions/services/transactions.service';
import { TransactionSalePayload } from '../../transactions/transaction.model';

/**
 * Max length of the ACH account holder name (NACHA Individual Name field is 22 chars).
 */
const ACH_HOLDER_NAME_MAX_LENGTH = 22;

/**
 * Characters that are NOT safe to send to the ACH gateway in the account holder name.
 *
 */
const ACH_HOLDER_NAME_DISALLOWED = /[^A-Za-z0-9 ]/g;

@Injectable({ providedIn: 'root' })
export class NachaWizardService {
  private transactionsService = inject(TransactionsService);
  private customersService = inject(CustomersService);

  /**
   * Sanitize a name for use as the ACH account holder name.
   */
  sanitizeAchAccountHolderName(name: string): string {
    return (name || '')
      .replace(ACH_HOLDER_NAME_DISALLOWED, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, ACH_HOLDER_NAME_MAX_LENGTH)
      .trim();
  }

  /**
   * Match NACHA rows to existing customers by `companyAccountNumber` (= row.individualIdNumber),
   * then reconcile each matched row against the customer's ACH account(s) on file:
   *
   * - no customer            → 'no-customer'
   * - customer, no ACH token → 'no-account'   (offer to save the file's account)
   * - customer, token matches → 'matched'      (charge the on-file token — same account)
   * - customer, none matches  → 'account-mismatch' (operator resolves per row / in bulk)
   *
   * On-file ACH accounts are fetched once per unique matched customer.
   */
  matchCustomers(rows: NachaRow[], customers: CustomerModel[]): Observable<NachaMatch[]> {
    if (rows.length === 0) return of([]);

    const matches: NachaMatch[] = rows.map((row) => {
      const customer = customers.find(
        (c) => c.companyAccountNumber?.toLowerCase() === row.individualIdNumber.toLowerCase()
      );

      if (!customer) {
        return {
          row,
          status: 'no-customer' as const,
          include: row.isDebit,
          error: 'Customer not found',
        };
      }

      return {
        row,
        customer,
        status: 'matched' as const,
        include: row.isDebit,
      };
    });

    // Fetch on-file ACH accounts once per unique matched customer, then classify.
    const customerIds = Array.from(
      new Set(
        matches
          .filter((m) => m.status === 'matched' && m.customer?.id)
          .map((m) => m.customer!.id as string)
      )
    );

    if (customerIds.length === 0) return of(matches);

    return forkJoin(
      customerIds.map((id) =>
        this.customersService.getAchPaymentMethods(id).pipe(
          map((tokens) => ({ id, tokens })),
          catchError(() => of({ id, tokens: [] as AchMethodModel[] }))
        )
      )
    ).pipe(
      map((results) => {
        const tokensByCustomer = new Map<string, AchMethodModel[]>(
          results.map((r) => [r.id, r.tokens])
        );

        for (const match of matches) {
          if (match.status !== 'matched' || !match.customer?.id) continue;

          const tokens = tokensByCustomer.get(match.customer.id) ?? [];
          match.achTokens = tokens;

          if (tokens.length === 0) {
            match.status = 'no-account';
            match.error = 'No ACH account on file';
            continue;
          }

          const matchedToken = tokens.find((t) => this.achAccountMatches(match.row, t));
          if (matchedToken) {
            // Same bank account as the file — charge the already-tokenized on-file account.
            match.matchedToken = matchedToken;
            match.selectedToken = matchedToken;
            match.accountSource = 'freely';
          } else {
            match.status = 'account-mismatch';
            match.error = 'ACH account differs from file';
          }
        }

        return matches;
      })
    );
  }

  /**
   * True only when the on-file ACH token matches the file's account on all four axes:
   * routing number, last-4, account length, and account holder name.
   *
   * The on-file account is masked, so its length is inferred from the masked string
   * (which preserves the original digit count).
   */
  achAccountMatches(row: NachaRow, token: AchMethodModel): boolean {
    const rowAccount = row.bankAccountNumber || '';
    const tokenMask = token.account || '';

    const routingMatch = this.digitsOnly(row.routingNumber) === this.digitsOnly(token.abaRoute);
    const last4Match = rowAccount.slice(-4) === tokenMask.slice(-4);
    const lengthMatch = rowAccount.length === tokenMask.length;
    const nameMatch =
      this.sanitizeAchAccountHolderName(row.individualName).toUpperCase() ===
      this.sanitizeAchAccountHolderName(token.accountHolderName || '').toUpperCase();

    return routingMatch && last4Match && lengthMatch && nameMatch;
  }

  private digitsOnly(value?: string): string {
    return (value || '').replace(/\D/g, '');
  }

  /**
   * Build the (plaintext) ACH payment-account payload for tokenizing a NACHA row's
   * bank account. Pass this to `SecureClientService.addAchToken`, which encrypts it
   * before it leaves the browser.
   */
  buildAchTokenPayload(row: NachaRow): {
    requestType: 'ACH';
    account: string;
    abaRoute: string;
    accountHolderName: string;
    accountType: 'BUSINESS' | 'CONSUMER';
    description: string;
  } {
    return {
      requestType: 'ACH',
      account: row.bankAccountNumber,
      abaRoute: row.routingNumber,
      accountHolderName: this.sanitizeAchAccountHolderName(row.individualName || row.rawIndividualName),
      // row.accountType (Checking/Savings) is the NACHA bank-account type, a distinct
      // concept from the account HOLDER type (Business/Consumer) this field now means —
      // NachaRow doesn't carry its batch's SEC code (PPD vs CCD) today, so this defaults
      // to Business (this codebase's existing B2B-first convention) until that's threaded
      // through the parser.
      accountType: 'BUSINESS',
      description: 'ACH PAYMENT',
    };
  }

  /**
   * Process all included matched rows in parallel as one-time ACH payments.
   * Uses routing/account data from the NACHA row directly — no stored token required.
   */
  processBulkAchPayments(matches: NachaMatch[]): Observable<NachaResult[]> {
    const readyMatches = matches.filter((m) => m.include && m.status === 'matched' && m.row.amount > 0);

    if (readyMatches.length === 0) return of([]);

    const paymentObservables = readyMatches.map((match) =>
      this.processSinglePayment(match).pipe(
        catchError((error) => {
          const decline = error?.data?.transactionResponse;
          const reason =
            decline?.verbiage ||
            error?.data?.message ||
            'The payment was not approved by the processor.';
          return of({
            match,
            status: 'failed' as const,
            transactionId: decline?.transactionId ?? undefined,
            error: reason,
            responseMessage: decline?.hardCode ?? undefined,
          });
        })
      )
    );

    return forkJoin(paymentObservables);
  }

  private processSinglePayment(match: NachaMatch): Observable<NachaResult> {
    const base: TransactionSalePayload = {
      customerId: match.customer!.id,
      reference: match.row.traceNumber,
      amount: match.row.amount,
      taxAmount: 0,
      shippingAmount: 0,
      costOfCapitalAmount: 0,
    };

    let payload: TransactionSalePayload;
    if (match.accountSource === 'freely' && match.selectedToken?.id) {
      // Charge the customer's stored ACH token (on-file account).
      payload = { ...base, tokenId: match.selectedToken.id };
    } else {
      // One-time ACH sale using the account printed in the NACHA file.
      const holderName = this.sanitizeAchAccountHolderName(
        match.row.individualName || match.row.rawIndividualName
      );
      payload = {
        ...base,
        paymentAccount: {
          requestType: 'ACH',
          account: match.row.bankAccountNumber,
          abaRoute: match.row.routingNumber,
          accountHolderName: holderName,
          // See buildAchTokenPayload's comment — defaults to Business pending SEC code
          // being threaded through the NACHA row model.
          accountType: 'BUSINESS',
        },
      };
    }

    return this.transactionsService.transactionSale(payload).pipe(
      map((transaction) => ({
        match,
        status: 'success' as const,
        transactionId: transaction.transactionId,
        responseMessage: transaction.resultCodeVerbaige,
      }))
    );
  }

  generateResultsCsv(matches: NachaMatch[], results: NachaResult[]): string {
    const headers = [
      'Row',
      'Individual ID',
      'Individual Name',
      'Amount',
      'Transaction Type',
      'Trace Number',
      'Status',
      'Transaction ID',
      'Error/Message',
    ];

    let csvContent = headers.join(',') + '\n';
    const resultsMap = new Map(results.map((r) => [r.match.row.rowIndex, r]));

    for (const match of matches) {
      const result = resultsMap.get(match.row.rowIndex);
      const status = result
        ? result.status === 'success'
          ? 'Approved'
          : result.status === 'failed'
            ? 'Declined'
            : result.status
        : match.include
          ? match.status
          : 'skipped';
      const transactionId = result?.transactionId ?? '';
      const message = result?.error ?? result?.responseMessage ?? match.error ?? '';

      const row = [
        match.row.rowIndex,
        `"${match.row.individualIdNumber}"`,
        `"${match.row.individualName}"`,
        match.row.amount.toFixed(2),
        `"${match.row.transactionTypeLabel}"`,
        `"${match.row.traceNumber}"`,
        status,
        transactionId,
        `"${message}"`,
      ].join(',');

      csvContent += row + '\n';
    }

    return csvContent;
  }

  downloadCsv(content: string, filename = 'nacha-results.csv'): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
